/**
 * Escalation contacts API (Feature 933 Phase 1).
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiPost, apiPut, apiRequest } from '../utils/apiClient';
import type {
  EscalationContact,
  EscalationContactPayload,
  EscalationContactStatus,
} from '../types/escalationContact.types';

interface ApiListResponse {
  success?: boolean;
  message?: string;
  count?: number;
  data?: EscalationContact[];
}

interface ApiItemResponse {
  success?: boolean;
  message?: string;
  validationErrors?: Array<{ field?: string; message: string }>;
  data?: EscalationContact;
}

export interface ListEscalationContactsParams {
  facilityId: number;
  status?: EscalationContactStatus;
  role?: string;
  search?: string;
}

function mapContact(raw: Record<string, unknown>): EscalationContact {
  return {
    id: Number(raw.id),
    facilityId: Number(raw.facilityId ?? raw.facility_id),
    name: String(raw.name ?? ''),
    role: raw.role as EscalationContact['role'],
    department: (raw.department as string) ?? null,
    mobileNumber: String(raw.mobileNumber ?? raw.mobile_number ?? ''),
    alternateMobileNumber: (raw.alternateMobileNumber ?? raw.alternate_mobile_number) as
      | string
      | null,
    status: (raw.status as EscalationContactStatus) ?? 'Active',
    createdAt: raw.createdAt as string | undefined,
    updatedAt: raw.updatedAt as string | undefined,
  };
}

export async function listEscalationContacts(
  params: ListEscalationContactsParams
): Promise<{ success: boolean; data: EscalationContact[]; error?: string }> {
  try {
    const qs = new URLSearchParams();
    qs.set('facilityId', String(params.facilityId));
    if (params.status) {
      qs.set('status', params.status);
    }
    if (params.role) {
      qs.set('role', params.role);
    }
    if (params.search) {
      qs.set('search', params.search);
    }

    const url = `${API_ENDPOINTS.ESCALATION_MATRIX.CONTACTS}?${qs.toString()}`;
    const response = await apiGet<ApiListResponse>(url, { requireAuth: true });
    const payload = response.data;

    if (payload?.success === false) {
      return { success: false, data: [], error: payload.message || 'Failed to load contacts' };
    }

    const rows = (payload?.data || []).map(item =>
      mapContact(item as unknown as Record<string, unknown>)
    );
    return { success: true, data: rows };
  } catch (err) {
    return {
      success: false,
      data: [],
      error: err instanceof Error ? err.message : 'Failed to load contacts',
    };
  }
}

export async function createEscalationContact(payload: EscalationContactPayload): Promise<{
  success: boolean;
  data?: EscalationContact;
  error?: string;
  fieldErrors?: Record<string, string>;
}> {
  try {
    const response = await apiPost<ApiItemResponse>(
      API_ENDPOINTS.ESCALATION_MATRIX.CONTACTS,
      payload,
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false || !body?.data) {
      const fieldErrors = Object.fromEntries(
        (body?.validationErrors || []).map(item => [item.field || 'form', item.message])
      );
      return {
        success: false,
        error: body?.message || 'Failed to create contact',
        fieldErrors,
      };
    }
    return { success: true, data: mapContact(body.data as unknown as Record<string, unknown>) };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to create contact',
    };
  }
}

export async function updateEscalationContact(
  id: number,
  payload: EscalationContactPayload
): Promise<{
  success: boolean;
  data?: EscalationContact;
  error?: string;
  fieldErrors?: Record<string, string>;
}> {
  try {
    const response = await apiPut<ApiItemResponse>(
      API_ENDPOINTS.ESCALATION_MATRIX.UPDATE_CONTACT(id),
      payload,
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false || !body?.data) {
      const fieldErrors = Object.fromEntries(
        (body?.validationErrors || []).map(item => [item.field || 'form', item.message])
      );
      return {
        success: false,
        error: body?.message || 'Failed to update contact',
        fieldErrors,
      };
    }
    return { success: true, data: mapContact(body.data as unknown as Record<string, unknown>) };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to update contact',
    };
  }
}

export async function patchEscalationContactStatus(
  id: number,
  facilityId: number,
  status: EscalationContactStatus
): Promise<{ success: boolean; data?: EscalationContact; error?: string }> {
  try {
    const response = await apiRequest<ApiItemResponse>(
      API_ENDPOINTS.ESCALATION_MATRIX.PATCH_CONTACT_STATUS(id),
      { method: 'PATCH', body: { facilityId, status }, requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false || !body?.data) {
      return { success: false, error: body?.message || 'Failed to update status' };
    }
    return { success: true, data: mapContact(body.data as unknown as Record<string, unknown>) };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to update status',
    };
  }
}
